<template>
  <el-container v-if="show" direction="vertical" class="survey-layout" :class="isPC || 'is-mobile'">
    <el-header v-if="isPC" class="layout-header" height="initial">
      <PersonalCommonHeader :companyId="companyId" :customPage="customPage" @changeLanguage="handleChangeLanguage"></PersonalCommonHeader>
    </el-header>
    <el-container class="layout-body" direction="horizontal" :style="isPC && isPreferencePage && 'height: auto'">
      <el-aside v-if="isPC" class="aside-menu custom-bg" width="auto">
        <div class="menu-list">
          <template v-for="(item, index) in menuList">
            <div
              v-if="item.show"
              class="menu-item"
              :key="index"
              @click="handleMenuClick(item)">
              {{ item.label }}
            </div>
          </template>
        </div>
      </el-aside>
      <el-container class="body-content" direction="vertical">
        <el-header class="survey-header" :height="surveyHeaderHeight" :class="isPreferencePage && 'preference-header' || 'question-header'">
          <div v-if="isPreferencePage && isFirstFinish" class="preference-header-mask">
            <div class="virtual-btn" @click="handleGoQuestionBtnClick"></div>
          </div>
          <div class="survey-image-wrapper">
            <img class="survey-image" :src="surveyImage" />
          </div>
          <div class="survey-title-container" :class="isPreferencePage && 'preference-title'">
            <div class="center-container">
              <div v-if="isPreferencePage" class="survey-title">{{ surveyTitle }}</div>
              <div
                v-if="isPreferencePage"
                class="go-question-btn"
                @click="handleGoQuestionBtnClick">
                {{ $t(`survey.btn.goQuestion.${surveyInfo.survey.has_answered ? 'modify' : 'new'}`) }}
              </div>
            </div>
          </div>
        </el-header>
        <router-view :showOverlay="!surveyInfo.survey.has_answered" :surveyInfo="surveyInfo"></router-view>
      </el-container>
    </el-container>
    <el-footer v-if="!isPC" class="layout-footer custom-bg" height="20px"></el-footer>
  </el-container>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import PersonalCommonHeader from '@/components/layout/header/personalCommonHeader'
import { deviceType } from '@/utils/common'
import { ACCOUNT_TYPE } from '@/constant/common'
import { addI18nProperty } from '@/i18n'

export default {
  beforeRouteEnter (to, from, next) {
    const pagePath = from.fullPath
    const ignorePathList = [
      '/recruiting/agreement-checking',
      '/recruiting/personal/resume/add',
      '/passport/'
    ]
    if (from.meta?.survey?.ignorePath || _.some(ignorePathList, ignorePath => pagePath.startsWith(ignorePath))) {
      next()
    } else {
      next(vm => {
        if (pagePath && pagePath !== '/') {
          vm.setFromPath(pagePath)
        }
      })
    }
  },
  beforeRouteLeave (to, from, next) {
    const pagePath = to.fullPath
    const ignorePathList = [
      '/recruiting/agreement-checking',
      '/recruiting/personal/resume/add',
      '/passport/'
    ]
    if (to.meta?.survey?.ignorePath || _.some(ignorePathList, ignorePath => pagePath.startsWith(ignorePath))) {
      next()
    } else {
      this.setFromPath(null)
      next()
    }
  },
  components: {
    PersonalCommonHeader
  },
  metaInfo () {
    return {
      style: this.styleList
    }
  },
  props: {
    isMSDCustom: {
      type: Boolean,
      default: false
    },
    customCompanyId: {
      required: false
    }
  },
  data () {
    return {
      show: false,
      surveyInfo: {},
      customPage: {}
    }
  },
  computed: {
    ...mapGetters('survey', ['fromPagePath']),
    isFirstFinish () {
      return this.$route.query.is_first === 'true' && this.$route.query.is_finish === 'true'
    },
    isPC () {
      return deviceType.isPc
    },
    companyId () {
      return this.customCompanyId || this.$route.params.companyId
    },
    isPreferencePage () {
      return this.$route.path.includes('/preference')
    },
    menuList () {
      const list = [
        {
          label: this.$t('survey.menu.home'),
          show: !this.isPreferencePage,
          path: this.isMSDCustom ? '/msdchina/join-talent-community' : `/recruiting/company/${this.companyId}/survey/`
        }
      ]
      return list
    },
    surveyHeaderHeight () {
      let height = 200
      if (this.isPreferencePage) {
        height = this.isPC ? 'auto' : 225
      } else {
        height = this.isPC ? 175 : 150
      }
      if (typeof height === 'number') {
        return `${height}px`
      } else {
        return height
      }
    },
    companyCustomColor () {
      return this.surveyInfo.company && this.surveyInfo.company.color
    },
    surveyTitle () {
      let title = ''
      if (this.isPreferencePage) {
        title = this.$t('survey.title.preference')
      }
      return title
    },
    surveyImage () {
      let imagePath = null
      if (this.isPC) {
        imagePath = this.surveyInfo.survey.img_for_pc
      } else {
        imagePath = this.surveyInfo.survey.img_for_mobile
      }
      return `${process.env.VUE_APP_QINIU_DOMAIN}${imagePath}`
    },
    styleList () {
      const list = [
        {
          vmid: 'customColor',
          innerHTML: `
          .custom-bg {
            background-color: ${this.companyCustomColor};
          }
          .custom-color {
            color: ${this.companyCustomColor};
          }
          `
        }
      ]
      return list
    }
  },
  created () {
    this.initSurveyInfo()
  },
  mounted () {
    this.$root.$el.style.height = window.innerHeight + 'px'
    window.onresize = () => {
      this.$root.$el.style.height = window.innerHeight + 'px'
    }
  },
  beforeDestroy () {
    window.onresize = null
  },
  methods: {
    ...mapActions('survey', ['getSurveyInfo', 'setFromPath']),
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('company', ['getCompanyBrandingPageInfo']),
    async initSurveyInfo () {
      this.show = false
      const currentAccount = this.getCurrentAccount()
      if (!currentAccount?.id) {
        // 2024-10-16 客户要求，未登录时访问 survey 连接跳到注册页
        this.$router.replace({ path: `/passport/${ACCOUNT_TYPE.CANDIDATE}/register`, query: { back: this.$route.fullPath, company_id: this.companyId } })
        return
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        await Promise.all([
          this.loadSurveyInfo(),
          this.loadCompanyPageInfo()
        ])
      } catch (err) {
        this.handleResponseError(err)
      } finally {
        this.$eventBus.$emit('showLoading', false)
        this.show = true
      }
    },
    handleMenuClick (menuItem) {
      this.$router.push(menuItem.path)
    },
    async loadSurveyInfo () {
      const resp = await this.getSurveyInfo(this.companyId)
      if (resp) {
        if (!resp.has_survey) {
          let path = ''
          if (this.isPC) {
            if (this.isMSDCustom) {
              path = '/msdchina/jobs'
            } else {
              path = `/recruiting/company/${this.companyId}/job-list`
            }
          } else {
            if (this.isMSDCustom) {
              path = '/msdchina/jobs'
            } else {
              path = `/mobile-web-more-jobs/${this.companyId}`
            }
          }
          this.$router.push({
            path: path,
            query: {
              company_id: this.companyId
            }
          })
          return
        }
        if (resp.show_policy) {
          const back = this.$route.fullPath
          let rejectBack = this.fromPagePath
          if (!rejectBack || rejectBack === '/') {
            const companyId = this.rootCompanyId || this.companyId
            if (this.isPC) {
              if (this.isMSDCustom) {
                rejectBack = '/msdchina/professionals'
              } else {
                rejectBack = `/company-detail-new/${companyId}/`
              }
            } else {
              if (this.isMSDCustom) {
                rejectBack = '/msdchina/jobs'
              } else {
                rejectBack = `/mobile-web-more-jobs/${companyId}`
              }
            }
          }
          this.$router.push({
            path: '/recruiting/agreement-checking',
            query: {
              company_id: this.companyId,
              back: back,
              reject_back: rejectBack
            }
          })
          return
        }
        if (!resp.survey.has_answered) {
          this.handleGoQuestionBtnClick()
        }
        this.surveyInfo = resp
      }
    },
    handleGoQuestionBtnClick () {
      let url = ''
      if (this.isMSDCustom) {
        url = '/msdchina/join-talent-community/question'
      } else {
        url = `/recruiting/company/${this.companyId}/survey/question`
      }
      this.$router.push({ path: url, query: this.$route.query })
    },
    handleResponseError (errorResp) {
      if ([20002006, 20002007].includes(errorResp.code)) {
        this.$router.replace(`/error/no-access-to-comapny-survey?companyId=${this.companyId}&type=${errorResp.code}`)
      }
    },
    async loadCompanyPageInfo () {
      if (!this.companyId) {
        return
      }
      const resp = await this.getCompanyBrandingPageInfo({
        company_id: this.isMSDCompany ? this.MSDCompanyId : this.companyId,
        language_code: this.$i18n.locale === 'en' ? 'en' : 'zh-cn'
      })
      if (resp) {
        const brandingPage = resp.branding_page
        const landingPage = resp.landing_page
        if (brandingPage) {
          addI18nProperty(brandingPage, 'i18nName', 'menu_cn_name', 'menu_en_name')
        }
        if (landingPage) {
          addI18nProperty(landingPage, 'i18nName', 'menu_cn_name', 'menu_en_name')
        }
        this.customPage = resp
      }
    },
    async handleChangeLanguage () {
      this.$eventBus.$emit('showLoading', true)
      try {
        await this.loadCompanyPageInfo()
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.survey-layout {
  height: 100%;
  font-size: 14px;

  .layout-header {
    padding: 0;

    ::v-deep {
      .navigation-container {
        .dropdown-list {
          box-sizing: border-box;
        }
      }
    }
  }

  .layout-body {
    height: calc(100% - 50px);

    .aside-menu {
      min-width: 150px;
      padding: 10px 0;

      .menu-list {
        margin-top: 30px;
        padding: 0 10px;
        text-align: center;

        .menu-item {
          display: inline-block;
          min-width: 80px;
          padding: 5px 0;
          background-color: #fff;
          cursor: pointer;
        }
      }
    }

    .body-content {
      .survey-header {
        padding: 0;
        position: relative;

        &.question-header {
          overflow: hidden;
        }

        &.preference-header {
          .preference-header-mask {
            width: 100%;
            height: 100%;
            background: #000;
            opacity: .5;
            position: absolute;
            top: 0;
            left: 0;
            z-index: 1;

            .virtual-btn {
              width: 110px;
              height: 50px;
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, 14%);
            }
          }
        }

        .survey-title-container {
          display: flex;
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;

          .center-container {
            flex: 1;
            margin: auto 0;
            text-align: center;

            .survey-title {
              font-weight: bold;
              font-size: 40px;
              color: #fff;
            }

            .go-question-btn {
              display: inline-block;
              margin-top: 15px;
              padding: 5px 15px;
              border: 1px solid #fff;
              background: #bdbcbb;
              font-weight: bold;
              font-size: 18px;
              color: #fff;
              cursor: pointer;
            }
          }
        }

        .survey-image-wrapper {
          width: 100%;
          padding-top: 33.67%;
          position: relative;
          overflow: hidden;

          .survey-image {
            width: 100%;
            height: auto;
            position: absolute;
            top: 0;
            left: 50%;
            transform: translateX(-50%);
          }
        }
      }
    }
  }

  &.is-mobile {
    .layout-body {
      .body-content {
        .survey-header {
          .survey-title-container {
            .center-container {
              .survey-title {
                text-align: left;
                margin-left: 20px;
                font-size: 26px;
              }

              .go-question-btn {
                font-size: 16px;
              }
            }

            &.preference-title {
              .center-container {
                .survey-title {
                  margin-left: 0;
                  text-align: center;
                }
              }
            }
          }

          .survey-image-wrapper {
            padding-top: 225px;
          }

          &.preference-header {
            .preference-header-mask {
              .virtual-btn {
                width: 95px;
                height: 40px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
